/**
 * Facility Management Industry Configuration
 *
 * Configuration for commercial / institutional building operations: HVAC,
 * elevators / escalators, building management systems (BMS), lighting,
 * fire & life-safety, plumbing and security.
 *
 * Sources for KPI ranges, compliance authorities and labor rates:
 *   - BOMA 2023 Experience Exchange Report
 *   - IFMA Operations & Maintenance Benchmarks 2024
 *   - JLL Real Estate Operations 2024
 *   - ASHRAE 90.1 / 62.1, NFPA 101 / 72, OSHA 1910, ADA, LEED v4.1
 */

import { IndustryConfig } from '../industryTypes'

export const facilityManagementConfig: IndustryConfig = {
  id: 'facility_management',
  name: 'Facility Management',
  description: 'Operations and maintenance for commercial / institutional buildings (HVAC, elevators, BMS, fire-life-safety, security)',
  icon: 'Building2',

  terminology: {
    asset: 'Equipment',
    assets: 'Equipment',
    assetPlural: 'Equipment',
    assetType: 'System Type',
    failure: 'Equipment Failure',
    health: 'Equipment Health',
    healthScore: 'Health Score',
    healthy: 'Operational',
    degraded: 'Performance Degraded',
    critical: 'Critical Issue',
    alert: 'Building Alert',
    process: 'Service',
    maintenance: 'Preventive Maintenance',
    inspection: 'Equipment Inspection',
    downtime: 'Service Interruption',
    workOrder: 'Work Order',
    prediction: 'Service Forecast',
    anomaly: 'Performance Anomaly',
    trend: 'Performance Trend',
    risk: 'Service Risk',
    facility: 'Building',
    facilityPlural: 'Buildings',
    location: 'Floor / Zone',
    operator: 'Building Engineer',
    supervisor: 'Facility Manager',
    calibration: 'Sensor Calibration',
    compliance: 'Code Compliance',
    criticality: 'Service Tier',
  },

  assetTypes: [
    {
      id: 'chiller',
      name: 'Chiller',
      icon: 'Snowflake',
      description: 'Centrifugal / screw / scroll chillers serving central plants',
      category: 'HVAC',
      defaultMetrics: ['supply_temp', 'return_temp', 'kw_per_ton', 'condenser_pressure', 'evaporator_pressure', 'runtime_hours'],
      maintenanceTypes: ['filter_replacement', 'refrigerant_service', 'tube_cleaning', 'oil_analysis', 'controls_calibration'],
      criticalityLevels: [
        { id: 'primary', name: 'Primary Chiller', color: '#ef4444', description: 'Lead chiller serving critical loads', downtimeCostMultiplier: 50 },
        { id: 'standby', name: 'Standby Chiller', color: '#f59e0b', description: 'Backup / lag chiller', downtimeCostMultiplier: 10 },
      ],
    },
    {
      id: 'air_handler',
      name: 'Air Handling Unit',
      icon: 'Wind',
      description: 'AHUs / VAV / RTU equipment delivering conditioned air',
      category: 'HVAC',
      defaultMetrics: ['supply_air_temp', 'return_air_temp', 'fan_speed', 'static_pressure', 'co2_ppm', 'filter_dp'],
      maintenanceTypes: ['filter_replacement', 'belt_inspection', 'coil_cleaning', 'damper_calibration', 'sensor_calibration'],
      criticalityLevels: [
        { id: 'critical_zone', name: 'Critical Zone AHU', color: '#ef4444', description: 'Serves data closet / lab / OR', downtimeCostMultiplier: 30 },
        { id: 'tenant', name: 'Tenant AHU', color: '#f59e0b', description: 'Serves leased space', downtimeCostMultiplier: 8 },
        { id: 'common_area', name: 'Common Area', color: '#3b82f6', description: 'Lobby / corridor / core', downtimeCostMultiplier: 3 },
      ],
    },
    {
      id: 'boiler',
      name: 'Boiler / Hot Water Plant',
      icon: 'Flame',
      description: 'Steam or hot-water boilers and associated heat exchangers',
      category: 'HVAC',
      defaultMetrics: ['stack_temp', 'supply_temp', 'return_temp', 'firing_rate', 'feedwater_conductivity', 'runtime_hours'],
      maintenanceTypes: ['water_treatment', 'tube_cleaning', 'burner_tuning', 'safety_inspection', 'combustion_test'],
      criticalityLevels: [
        { id: 'primary', name: 'Primary Boiler', color: '#ef4444', description: 'Lead boiler', downtimeCostMultiplier: 25 },
        { id: 'standby', name: 'Standby Boiler', color: '#f59e0b', description: 'N+1 backup', downtimeCostMultiplier: 5 },
      ],
    },
    {
      id: 'elevator',
      name: 'Elevator / Escalator',
      icon: 'ArrowUpDown',
      description: 'Vertical transportation — passenger and freight elevators, escalators',
      category: 'Vertical Transportation',
      defaultMetrics: ['runtime_hours', 'door_cycles', 'callback_count', 'rope_tension', 'brake_wear', 'travel_speed'],
      maintenanceTypes: ['monthly_inspection', 'rope_lubrication', 'brake_adjustment', 'door_alignment', 'controller_test'],
      criticalityLevels: [
        { id: 'main', name: 'Main Passenger', color: '#ef4444', description: 'Primary passenger car', downtimeCostMultiplier: 40 },
        { id: 'service', name: 'Service / Freight', color: '#f59e0b', description: 'Service or freight car', downtimeCostMultiplier: 15 },
        { id: 'escalator', name: 'Escalator', color: '#3b82f6', description: 'Escalator', downtimeCostMultiplier: 10 },
      ],
    },
    {
      id: 'fire_life_safety',
      name: 'Fire / Life-Safety System',
      icon: 'Shield',
      description: 'Fire alarm panels, sprinklers, smoke control, emergency lighting',
      category: 'Life-Safety',
      defaultMetrics: ['supervisory_status', 'trouble_signals', 'battery_voltage', 'sprinkler_pressure', 'inspection_due'],
      maintenanceTypes: ['monthly_test', 'quarterly_test', 'annual_inspection', 'battery_replacement', 'sprinkler_flow_test'],
      criticalityLevels: [
        { id: 'life_safety', name: 'Life-Safety Critical', color: '#ef4444', description: 'NFPA 101 mandated equipment', downtimeCostMultiplier: 100 },
      ],
    },
    {
      id: 'bms_controller',
      name: 'BMS Controller',
      icon: 'Cpu',
      description: 'Building automation controllers (BACnet / Modbus) and field devices',
      category: 'Controls',
      defaultMetrics: ['cpu_load', 'point_count_online', 'comm_errors', 'alarm_count', 'overrides_active'],
      maintenanceTypes: ['firmware_update', 'point_calibration', 'graphic_update', 'integration_test'],
      criticalityLevels: [
        { id: 'plant', name: 'Plant Controller', color: '#ef4444', description: 'Central plant supervisor', downtimeCostMultiplier: 35 },
        { id: 'zone', name: 'Zone Controller', color: '#f59e0b', description: 'Field zone controller', downtimeCostMultiplier: 8 },
      ],
    },
    {
      id: 'lighting_system',
      name: 'Lighting System',
      icon: 'Lightbulb',
      description: 'Interior / exterior lighting and occupancy / daylight controls',
      category: 'Electrical',
      defaultMetrics: ['runtime_hours', 'lumen_depreciation', 'driver_temp', 'occupancy_overrides', 'daylight_setpoint'],
      maintenanceTypes: ['lamp_replacement', 'driver_replacement', 'controls_commissioning', 'photocell_calibration'],
      criticalityLevels: [
        { id: 'egress', name: 'Egress / Exit Lighting', color: '#ef4444', description: 'Life-safety egress', downtimeCostMultiplier: 50 },
        { id: 'tenant', name: 'Tenant Lighting', color: '#f59e0b', description: 'Leased-space lighting', downtimeCostMultiplier: 5 },
        { id: 'exterior', name: 'Exterior / Site', color: '#3b82f6', description: 'Parking / facade', downtimeCostMultiplier: 3 },
      ],
    },
    {
      id: 'plumbing_pump',
      name: 'Pump / Plumbing System',
      icon: 'Droplet',
      description: 'Domestic water pumps, sump pumps, sewage ejectors, water heaters',
      category: 'Plumbing',
      defaultMetrics: ['discharge_pressure', 'suction_pressure', 'flow_rate', 'motor_amps', 'runtime_hours', 'vibration'],
      maintenanceTypes: ['seal_replacement', 'bearing_lube', 'impeller_inspection', 'tank_cleaning', 'coil_descaling'],
      criticalityLevels: [
        { id: 'domestic', name: 'Domestic Water', color: '#ef4444', description: 'Potable supply', downtimeCostMultiplier: 30 },
        { id: 'sanitary', name: 'Sanitary / Sewage', color: '#f59e0b', description: 'Sanitary ejector', downtimeCostMultiplier: 20 },
        { id: 'storm', name: 'Storm / Sump', color: '#3b82f6', description: 'Storm drainage', downtimeCostMultiplier: 8 },
      ],
    },
    {
      id: 'security_system',
      name: 'Security & Access Control',
      icon: 'Lock',
      description: 'Card-reader / camera / intrusion / video-management systems',
      category: 'Security',
      defaultMetrics: ['reader_online', 'camera_uptime', 'storage_remaining_days', 'alarm_count', 'failed_access_attempts'],
      maintenanceTypes: ['firmware_update', 'lens_cleaning', 'reader_test', 'storage_audit'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical Access Point', color: '#ef4444', description: 'Server room / vault', downtimeCostMultiplier: 50 },
        { id: 'perimeter', name: 'Perimeter', color: '#f59e0b', description: 'Lobby / loading dock', downtimeCostMultiplier: 15 },
        { id: 'standard', name: 'Standard', color: '#3b82f6', description: 'Internal door', downtimeCostMultiplier: 4 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'system_availability',
      name: 'System Availability',
      icon: 'Activity',
      description: 'Equipment uptime during occupied hours',
      category: 'universal',
      kpis: [
        { id: 'systems_uptime', name: 'Systems Uptime', unit: '%', description: 'Building systems uptime during occupied hours', thresholds: { critical: { min: 0, max: 95 }, warning: { min: 95, max: 99 }, normal: { min: 99, max: 100 } }, target: 99, higherIsBetter: true },
        { id: 'elevator_availability', name: 'Elevator Availability', unit: '%', description: 'Elevators in service vs scheduled', thresholds: { critical: { min: 0, max: 92 }, warning: { min: 92, max: 96 }, normal: { min: 96, max: 100 } }, target: 99, higherIsBetter: true },
        { id: 'mttr_hours', name: 'Mean Time To Repair', unit: 'hours', description: 'Average WO close time', thresholds: { critical: { min: 8, max: 72 }, warning: { min: 4, max: 8 }, normal: { min: 0, max: 4 } }, target: 4, higherIsBetter: false },
      ],
    },
    {
      id: 'energy_performance',
      name: 'Energy Performance',
      icon: 'Zap',
      description: 'Building energy efficiency',
      category: 'industry_specific',
      kpis: [
        { id: 'eui', name: 'Energy Use Intensity', unit: 'kBtu/sf/yr', description: 'ENERGY STAR-style site EUI', thresholds: { critical: { min: 110, max: 300 }, warning: { min: 80, max: 110 }, normal: { min: 0, max: 80 } }, target: 60, higherIsBetter: false },
        { id: 'energy_star_score', name: 'ENERGY STAR Score', unit: 'score', description: '1-100 percentile vs peer buildings', thresholds: { critical: { min: 0, max: 50 }, warning: { min: 50, max: 75 }, normal: { min: 75, max: 100 } }, target: 85, higherIsBetter: true },
        { id: 'chiller_efficiency', name: 'Chiller Efficiency', unit: 'kW/ton', description: 'Plant kW per ton of cooling', thresholds: { critical: { min: 1.0, max: 2.5 }, warning: { min: 0.7, max: 1.0 }, normal: { min: 0, max: 0.7 } }, target: 0.55, higherIsBetter: false },
        { id: 'water_use_intensity', name: 'Water Use Intensity', unit: 'gal/sf/yr', description: 'Annual water per square foot', thresholds: { critical: { min: 25, max: 100 }, warning: { min: 15, max: 25 }, normal: { min: 0, max: 15 } }, target: 12, higherIsBetter: false },
      ],
    },
    {
      id: 'occupant_experience',
      name: 'Occupant Experience',
      icon: 'Users',
      description: 'Tenant comfort and indoor environmental quality',
      category: 'industry_specific',
      kpis: [
        { id: 'comfort_complaints', name: 'Comfort Complaints', unit: 'per 1K sf/yr', description: 'Hot/cold/IAQ tickets per 1,000 sf', thresholds: { critical: { min: 3, max: 50 }, warning: { min: 1, max: 3 }, normal: { min: 0, max: 1 } }, target: 0.5, higherIsBetter: false },
        { id: 'co2_excursion_pct', name: 'CO₂ Excursion Time', unit: '%', description: 'Occupied hours CO₂ above 1,000 ppm', thresholds: { critical: { min: 5, max: 50 }, warning: { min: 1, max: 5 }, normal: { min: 0, max: 1 } }, target: 0, higherIsBetter: false },
        { id: 'temp_setpoint_compliance', name: 'Setpoint Compliance', unit: '%', description: 'Time within ±2°F of setpoint', thresholds: { critical: { min: 0, max: 85 }, warning: { min: 85, max: 95 }, normal: { min: 95, max: 100 } }, target: 98, higherIsBetter: true },
      ],
    },
    {
      id: 'compliance_safety',
      name: 'Compliance & Safety',
      icon: 'Shield',
      description: 'Code, life-safety and environmental compliance',
      category: 'universal',
      kpis: [
        { id: 'fire_safety_compliance', name: 'Fire/Life-Safety Inspection Pass', unit: '%', description: 'AHJ-led inspection pass rate', thresholds: { critical: { min: 0, max: 95 }, warning: { min: 95, max: 99 }, normal: { min: 99, max: 100 } }, target: 100, higherIsBetter: true },
        { id: 'pm_compliance', name: 'PM Compliance', unit: '%', description: 'Preventive maintenance completed on time', thresholds: { critical: { min: 0, max: 80 }, warning: { min: 80, max: 95 }, normal: { min: 95, max: 100 } }, target: 98, higherIsBetter: true },
        { id: 'permit_compliance', name: 'Permit & Cert Currency', unit: '%', description: 'Operating certificates current (elevators, boilers, fire)', thresholds: { critical: { min: 0, max: 95 }, warning: { min: 95, max: 99 }, normal: { min: 99, max: 100 } }, target: 100, higherIsBetter: true },
      ],
    },
    {
      id: 'cost_efficiency',
      name: 'Cost Efficiency',
      icon: 'DollarSign',
      description: 'Operating cost performance',
      category: 'industry_specific',
      kpis: [
        { id: 'opex_per_sf', name: 'Operating Cost per SF', unit: '$/sf/yr', description: 'Annual OpEx per square foot (BOMA)', thresholds: { critical: { min: 12, max: 30 }, warning: { min: 8, max: 12 }, normal: { min: 0, max: 8 } }, target: 7.5, higherIsBetter: false },
        { id: 'wo_close_rate', name: 'WO Close Rate', unit: '%', description: 'Work orders closed within SLA', thresholds: { critical: { min: 0, max: 80 }, warning: { min: 80, max: 95 }, normal: { min: 95, max: 100 } }, target: 95, higherIsBetter: true },
        { id: 'reactive_to_planned_ratio', name: 'Reactive : Planned Ratio', unit: '%', description: 'Reactive maintenance hours / total hours', thresholds: { critical: { min: 40, max: 100 }, warning: { min: 25, max: 40 }, normal: { min: 0, max: 25 } }, target: 20, higherIsBetter: false },
      ],
    },
  ],

  maintenanceTypes: [
    { id: 'preventive_pm', name: 'Preventive Maintenance', icon: 'Calendar', description: 'Scheduled task per OEM / standard', estimatedDuration: { min: 30, typical: 90, max: 240, unit: 'minutes' }, requiredSkills: ['mechanical', 'electrical'], downtime: false, criticality: 'medium' },
    { id: 'filter_replacement', name: 'Filter Replacement', icon: 'Filter', description: 'AHU / fan-coil / chiller filter swap', estimatedDuration: { min: 15, typical: 30, max: 60, unit: 'minutes' }, requiredSkills: ['hvac'], downtime: false, criticality: 'low' },
    { id: 'belt_inspection', name: 'Belt / Drive Inspection', icon: 'Settings', description: 'Belt tension + pulley alignment', estimatedDuration: { min: 30, typical: 45, max: 90, unit: 'minutes' }, requiredSkills: ['hvac'], downtime: false, criticality: 'low' },
    { id: 'controls_calibration', name: 'BMS / Sensor Calibration', icon: 'Activity', description: 'Calibrate temp / pressure / CO₂ sensors', estimatedDuration: { min: 60, typical: 120, max: 240, unit: 'minutes' }, requiredSkills: ['controls'], downtime: false, criticality: 'medium' },
    { id: 'elevator_safety_test', name: 'Elevator Safety Test', icon: 'Shield', description: 'Annual / 5-yr load + governor test (ASME A17.1)', estimatedDuration: { min: 120, typical: 240, max: 480, unit: 'minutes' }, requiredSkills: ['elevator', 'inspection'], downtime: true, criticality: 'critical' },
    { id: 'fire_alarm_test', name: 'Fire Alarm Test', icon: 'Bell', description: 'NFPA 72 monthly / quarterly / annual tests', estimatedDuration: { min: 60, typical: 180, max: 480, unit: 'minutes' }, requiredSkills: ['fire_life_safety'], downtime: false, criticality: 'critical' },
    { id: 'sprinkler_flow_test', name: 'Sprinkler Flow Test', icon: 'Droplet', description: 'NFPA 25 inspection + flow test', estimatedDuration: { min: 90, typical: 180, max: 360, unit: 'minutes' }, requiredSkills: ['fire_life_safety', 'plumbing'], downtime: false, criticality: 'critical' },
    { id: 'water_treatment', name: 'Water Treatment', icon: 'Droplet', description: 'Cooling-tower / boiler chemical service', estimatedDuration: { min: 60, typical: 120, max: 240, unit: 'minutes' }, requiredSkills: ['hvac', 'chemistry'], downtime: false, criticality: 'high' },
    { id: 'lamp_relamp', name: 'Group Re-lamping', icon: 'Lightbulb', description: 'Scheduled mass lamp / driver replacement', estimatedDuration: { min: 240, typical: 480, max: 960, unit: 'minutes' }, requiredSkills: ['electrical'], downtime: false, criticality: 'low' },
    { id: 'corrective_repair', name: 'Corrective Repair', icon: 'Wrench', description: 'Repair following alarm / complaint', estimatedDuration: { min: 30, typical: 120, max: 480, unit: 'minutes' }, requiredSkills: ['mechanical', 'electrical'], downtime: true, criticality: 'high' },
  ],

  riskFactors: [
    { id: 'aging_chiller', name: 'Aging Chiller / Boiler', description: 'Equipment past expected useful life', severity: 'high', calculation: 'age_based' },
    { id: 'elevator_overdue_inspection', name: 'Overdue Elevator Inspection', severity: 'critical', description: 'AHJ certificate expiring', calculation: 'compliance_based' },
    { id: 'fire_system_supervisory', name: 'Fire System Supervisory Trouble', severity: 'critical', description: 'Trouble signals on fire panel', calculation: 'sensor_based' },
    { id: 'iaq_excursion', name: 'IAQ Excursion Pattern', severity: 'high', description: 'Recurring CO₂ / VOC / RH excursions', calculation: 'threshold_based' },
    { id: 'energy_drift', name: 'Energy Performance Drift', severity: 'medium', description: 'EUI trending above target', calculation: 'trend_based' },
    { id: 'water_intrusion_risk', name: 'Water Intrusion Risk', severity: 'high', description: 'Roof / mechanical / plumbing leak history', calculation: 'incident_based' },
    { id: 'access_credential_drift', name: 'Access Credential Drift', severity: 'medium', description: 'Stale or excessive credentials', calculation: 'compliance_based' },
  ],

  complianceStandards: [
    { id: 'ashrae_90_1', name: 'ASHRAE 90.1', description: 'Energy standard for buildings except low-rise residential', authority: 'ASHRAE', criticality: 'high' },
    { id: 'ashrae_62_1', name: 'ASHRAE 62.1', description: 'Ventilation for acceptable indoor air quality', authority: 'ASHRAE', criticality: 'high' },
    { id: 'nfpa_72', name: 'NFPA 72 — National Fire Alarm Code', description: 'Fire alarm and signaling systems', authority: 'NFPA', criticality: 'critical' },
    { id: 'nfpa_25', name: 'NFPA 25 — Inspection of Water-Based Fire Protection', description: 'Sprinkler / standpipe / pump inspection', authority: 'NFPA', criticality: 'critical' },
    { id: 'nfpa_101', name: 'NFPA 101 — Life-Safety Code', description: 'Egress, fire-resistance, life-safety', authority: 'NFPA', criticality: 'critical' },
    { id: 'asme_a17_1', name: 'ASME A17.1 — Elevator Safety Code', description: 'Elevators and escalators', authority: 'ASME', criticality: 'critical' },
    { id: 'osha_1910', name: 'OSHA 29 CFR 1910', description: 'General industry occupational safety', authority: 'OSHA', criticality: 'high' },
    { id: 'ada', name: 'Americans with Disabilities Act', description: 'Accessibility standards for built environment', authority: 'DOJ', criticality: 'high' },
    { id: 'leed_v41', name: 'LEED v4.1 O+M', description: 'Leadership in Energy and Environmental Design — Operations and Maintenance', authority: 'USGBC', criticality: 'medium' },
    { id: 'energy_star_buildings', name: 'ENERGY STAR for Buildings', description: 'Whole-building energy benchmarking', authority: 'EPA', criticality: 'medium' },
  ],

  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Equipment Health Summary',
      context: 'asset_health',
      audience: ['executive', 'manager', 'engineer'],
      template: '{assetType} "{assetName}" is currently {healthStatus}. {trendDescription} Over the past {timeRange}, {kpiSummary}. {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'timeRange', type: 'time' },
        { name: 'kpiSummary', type: 'metric' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
    {
      id: 'comfort_alert',
      name: 'Occupant Comfort Alert',
      context: 'prediction',
      audience: ['manager', 'engineer', 'technician'],
      template: 'Comfort risk detected on {location}: {predictionDetails}. Confidence: {confidence}%. Recommended action: {recommendation}',
      variables: [
        { name: 'location', type: 'terminology' },
        { name: 'predictionDetails', type: 'metric' },
        { name: 'confidence', type: 'percentage' },
        { name: 'recommendation', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '7d',
    refreshInterval: 60,
    defaultDashboard: 'command-center',
  },

  professionalRoles: [
    {
      id: 'building_engineer',
      category: 'sme',
      name: 'Building Engineer',
      shortName: 'BE',
      description: 'Operates and maintains MEP systems and BMS for one or more buildings',
      responsibilities: [
        'Daily round inspections',
        'BMS alarm response',
        'Preventive maintenance execution',
        'Tenant comfort calls',
        'Vendor escort and oversight',
      ],
      focusAreas: ['equipment_health', 'iaq', 'energy', 'work_orders', 'safety'],
      explanationStyle: {
        depth: 'standard',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'operator',
        focusOn: ['action_items', 'safety'],
      },
      decisionAuthority: ['shutdown_in_emergency', 'pm_completion', 'vendor_dispatch_low'],
      reportingTo: 'Chief Engineer',
      icon: 'Wrench',
    },
    {
      id: 'controls_specialist',
      category: 'sme',
      name: 'Controls Specialist',
      shortName: 'CS',
      description: 'Manages BMS / BACnet / integration platforms and analytics',
      responsibilities: [
        'BMS programming and graphics',
        'Sensor calibration',
        'Sequence-of-operations tuning',
        'Analytics rule authoring',
        'IT/OT integration',
      ],
      focusAreas: ['controls_health', 'energy_optimization', 'data_quality'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'risk'],
      },
      decisionAuthority: ['controls_program_change', 'analytics_rule_publish'],
      reportingTo: 'Chief Engineer',
      icon: 'Cpu',
    },
    {
      id: 'fls_inspector',
      category: 'technician',
      name: 'Fire/Life-Safety Inspector',
      shortName: 'FLSI',
      description: 'Conducts NFPA-mandated inspections and tests',
      responsibilities: [
        'Monthly fire-alarm tests',
        'Quarterly / annual sprinkler tests',
        'Emergency lighting verification',
        'AHJ liaison and documentation',
      ],
      focusAreas: ['life_safety_compliance', 'inspection_due_dates'],
      explanationStyle: {
        depth: 'standard',
        includeMetrics: false,
        includeCosts: false,
        includeRootCause: false,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'operator',
        focusOn: ['safety', 'compliance'],
      },
      decisionAuthority: ['out_of_service_tagging', 'ahj_notification'],
      reportingTo: 'Facility Manager',
      icon: 'Shield',
    },
    {
      id: 'facility_manager',
      category: 'management',
      name: 'Facility Manager',
      shortName: 'FM',
      description: 'Owns operations, vendor performance, and budget for the building portfolio',
      responsibilities: [
        'Operating budget and forecast',
        'Vendor / contract management',
        'Tenant relationship and SLAs',
        'Capital planning input',
        'Sustainability reporting',
      ],
      focusAreas: ['opex', 'sla_compliance', 'tenant_satisfaction', 'energy_performance', 'capex_runway'],
      explanationStyle: {
        depth: 'summary',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive',
        focusOn: ['business_impact', 'risk', 'compliance'],
      },
      decisionAuthority: ['vendor_selection', 'pm_program_change', 'budget_reallocation'],
      reportingTo: 'Director of Real Estate',
      icon: 'Briefcase',
    },
    {
      id: 'sustainability_manager',
      category: 'management',
      name: 'Sustainability / ESG Manager',
      shortName: 'SM',
      description: 'Owns ENERGY STAR / LEED / GHG reporting and decarbonization roadmap',
      responsibilities: [
        'Energy and water benchmarking',
        'GHG emissions reporting',
        'LEED / WELL certification',
        'Decarbonization roadmap',
        'Tenant engagement programs',
      ],
      focusAreas: ['eui', 'ghg_intensity', 'water_intensity', 'certification_currency'],
      explanationStyle: {
        depth: 'summary',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive',
        focusOn: ['business_impact', 'compliance'],
      },
      decisionAuthority: ['certification_pursuit', 'esg_disclosures'],
      reportingTo: 'Chief Sustainability Officer',
      icon: 'Leaf',
    },
    {
      id: 'director_of_real_estate',
      category: 'executive',
      name: 'Director of Real Estate',
      shortName: 'DRE',
      description: 'Owns total cost, portfolio strategy and capital allocation',
      responsibilities: [
        'Portfolio strategy',
        'Capital allocation across buildings',
        'Disposal / acquisition decisions',
        'Tenant retention strategy',
      ],
      focusAreas: ['portfolio_opex_per_sf', 'capex_pipeline', 'occupancy', 'tenant_retention'],
      explanationStyle: {
        depth: 'high-level',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive',
        focusOn: ['business_impact', 'risk'],
      },
      decisionAuthority: ['portfolio_strategy', 'major_capex', 'asset_disposition'],
      reportingTo: 'COO',
      icon: 'Crown',
    },
  ],

  partsCatalog: {
    categories: [
      {
        id: 'hvac',
        name: 'HVAC Components',
        parts: [
          { id: 'merv13_filter', name: 'MERV-13 Pleated Filter (24x24x4)', sku: 'FM-FLT-M13-2424', unitCost: 38, leadTimeDays: 3, compatibleAssetTypes: ['air_handler', 'chiller'], failureModes: ['clogging', 'bypass'] },
          { id: 'vfd_drive', name: 'Variable Frequency Drive (10HP)', sku: 'FM-VFD-010', unitCost: 1200, leadTimeDays: 10, compatibleAssetTypes: ['air_handler', 'plumbing_pump'], failureModes: ['capacitor_aging', 'overheating'] },
          { id: 'compressor_scroll', name: 'Scroll Compressor (chiller)', sku: 'FM-CMP-SCR', unitCost: 4800, leadTimeDays: 21, compatibleAssetTypes: ['chiller'], failureModes: ['scroll_wear', 'refrigerant_leak'] },
          { id: 'ahu_belt', name: 'AHU Drive Belt (B-section)', sku: 'FM-BLT-B', unitCost: 28, leadTimeDays: 2, compatibleAssetTypes: ['air_handler'], failureModes: ['wear', 'breakage'] },
          { id: 'damper_actuator', name: 'Damper Actuator (24V)', sku: 'FM-ACT-024', unitCost: 180, leadTimeDays: 5, compatibleAssetTypes: ['air_handler', 'bms_controller'], failureModes: ['gearset_wear', 'feedback_drift'] },
          { id: 'temp_sensor', name: 'Duct Temp Sensor (RTD)', sku: 'FM-SEN-T', unitCost: 65, leadTimeDays: 3, compatibleAssetTypes: ['air_handler', 'bms_controller'], failureModes: ['drift', 'open_circuit'] },
        ],
      },
      {
        id: 'plumbing',
        name: 'Plumbing Components',
        parts: [
          { id: 'pump_seal', name: 'Pump Mechanical Seal', sku: 'FM-SEL-MEC', unitCost: 220, leadTimeDays: 7, compatibleAssetTypes: ['plumbing_pump'], failureModes: ['leak', 'face_wear'] },
          { id: 'pump_bearing', name: 'Pump Bearing Set', sku: 'FM-BRG-PMP', unitCost: 140, leadTimeDays: 5, compatibleAssetTypes: ['plumbing_pump'], failureModes: ['bearing_wear', 'lubrication_loss'] },
          { id: 'tankless_heat_exchanger', name: 'Tankless Heat Exchanger', sku: 'FM-HEX-TKL', unitCost: 920, leadTimeDays: 14, compatibleAssetTypes: ['plumbing_pump'], failureModes: ['scale_buildup', 'pinhole_leak'] },
        ],
      },
      {
        id: 'electrical_lighting',
        name: 'Electrical & Lighting',
        parts: [
          { id: 'led_panel', name: 'LED Troffer Panel (2x4)', sku: 'FM-LED-2X4', unitCost: 95, leadTimeDays: 5, compatibleAssetTypes: ['lighting_system'], failureModes: ['driver_failure', 'lumen_depreciation'] },
          { id: 'led_driver', name: 'LED Driver (50W constant-current)', sku: 'FM-DRV-050', unitCost: 42, leadTimeDays: 5, compatibleAssetTypes: ['lighting_system'], failureModes: ['capacitor_aging', 'thermal_failure'] },
          { id: 'occupancy_sensor', name: 'Occupancy / Daylight Sensor', sku: 'FM-OCC-001', unitCost: 75, leadTimeDays: 5, compatibleAssetTypes: ['lighting_system', 'bms_controller'], failureModes: ['detection_drift', 'comm_loss'] },
          { id: 'breaker_20a', name: 'Breaker 20A 1P', sku: 'FM-BRK-020', unitCost: 35, leadTimeDays: 3, compatibleAssetTypes: ['lighting_system', 'plumbing_pump'], failureModes: ['nuisance_trip', 'contact_wear'] },
        ],
      },
      {
        id: 'life_safety',
        name: 'Fire / Life-Safety',
        parts: [
          { id: 'smoke_detector', name: 'Photoelectric Smoke Detector', sku: 'FM-SMK-PHOTO', unitCost: 95, leadTimeDays: 5, compatibleAssetTypes: ['fire_life_safety'], failureModes: ['contamination', 'sensitivity_drift'] },
          { id: 'flow_switch', name: 'Sprinkler Flow Switch', sku: 'FM-FLW-SPR', unitCost: 220, leadTimeDays: 7, compatibleAssetTypes: ['fire_life_safety'], failureModes: ['paddle_seizure', 'contact_failure'] },
          { id: 'alarm_battery', name: 'Fire Alarm Standby Battery (12V 18Ah)', sku: 'FM-BAT-FAL', unitCost: 85, leadTimeDays: 5, compatibleAssetTypes: ['fire_life_safety'], failureModes: ['capacity_loss', 'sulfation'] },
        ],
      },
      {
        id: 'controls',
        name: 'Controls & BMS',
        parts: [
          { id: 'bms_controller_zone', name: 'Zone Controller (BACnet)', sku: 'FM-BMS-ZN', unitCost: 480, leadTimeDays: 10, compatibleAssetTypes: ['bms_controller'], failureModes: ['firmware_corruption', 'comm_loss'] },
          { id: 'bms_router', name: 'BMS Router / Gateway', sku: 'FM-BMS-RTR', unitCost: 1200, leadTimeDays: 14, compatibleAssetTypes: ['bms_controller'], failureModes: ['power_supply', 'comm_loss'] },
        ],
      },
    ],
  },

  rootCauseLibrary: {
    categories: [
      {
        id: 'hvac_performance',
        name: 'HVAC Performance',
        causes: [
          { id: 'fouled_coils', name: 'Fouled Coils (low ΔT)', severity: 'medium', frequency: 'common', assetTypes: ['air_handler', 'chiller'] },
          { id: 'low_refrigerant_charge', name: 'Low Refrigerant Charge', severity: 'high', frequency: 'uncommon', assetTypes: ['chiller'] },
          { id: 'simultaneous_heat_cool', name: 'Simultaneous Heating + Cooling', severity: 'medium', frequency: 'common', assetTypes: ['air_handler', 'bms_controller'] },
          { id: 'damper_stuck', name: 'Damper Stuck / Mis-positioned', severity: 'medium', frequency: 'common', assetTypes: ['air_handler'] },
          { id: 'failed_econo_changeover', name: 'Failed Economizer Changeover', severity: 'medium', frequency: 'uncommon', assetTypes: ['air_handler'] },
        ],
      },
      {
        id: 'controls_data',
        name: 'Controls & Data',
        causes: [
          { id: 'sensor_drift', name: 'Sensor Drift / Failure', severity: 'medium', frequency: 'common', assetTypes: ['bms_controller', 'air_handler'] },
          { id: 'override_left_active', name: 'Manual Override Left Active', severity: 'medium', frequency: 'common', assetTypes: ['bms_controller'] },
          { id: 'comm_loss', name: 'Field Bus Communication Loss', severity: 'high', frequency: 'uncommon', assetTypes: ['bms_controller'] },
          { id: 'incorrect_schedule', name: 'Incorrect Occupancy Schedule', severity: 'medium', frequency: 'common', assetTypes: ['bms_controller', 'air_handler', 'lighting_system'] },
        ],
      },
      {
        id: 'mechanical_wear',
        name: 'Mechanical Wear',
        causes: [
          { id: 'belt_slip', name: 'Belt Slip / Misalignment', severity: 'medium', frequency: 'common', assetTypes: ['air_handler'] },
          { id: 'bearing_wear', name: 'Bearing Wear (vibration trend)', severity: 'high', frequency: 'periodic', assetTypes: ['plumbing_pump', 'air_handler'] },
          { id: 'pump_cavitation', name: 'Pump Cavitation', severity: 'high', frequency: 'uncommon', assetTypes: ['plumbing_pump'] },
        ],
      },
      {
        id: 'life_safety',
        name: 'Life-Safety',
        causes: [
          { id: 'detector_contamination', name: 'Detector Contamination', severity: 'high', frequency: 'uncommon', assetTypes: ['fire_life_safety'] },
          { id: 'standby_battery_eol', name: 'Fire Panel Battery End-of-Life', severity: 'critical', frequency: 'periodic', assetTypes: ['fire_life_safety'] },
          { id: 'sprinkler_obstruction', name: 'Sprinkler Obstruction (storage / fixtures)', severity: 'high', frequency: 'uncommon', assetTypes: ['fire_life_safety'] },
        ],
      },
      {
        id: 'envelope_water',
        name: 'Envelope & Water',
        causes: [
          { id: 'roof_leak', name: 'Roof Leak', severity: 'high', frequency: 'uncommon', assetTypes: ['plumbing_pump'] },
          { id: 'condensate_overflow', name: 'AHU Condensate Pan Overflow', severity: 'medium', frequency: 'common', assetTypes: ['air_handler'] },
          { id: 'envelope_air_leak', name: 'Envelope Air Leak / Stack Effect', severity: 'medium', frequency: 'common', assetTypes: ['air_handler'] },
        ],
      },
    ],
  },

  laborRates: {
    currency: 'USD',
    roles: [
      { id: 'building_engineer_jr', name: 'Building Engineer (Junior)', hourlyRate: 38, overtimeMultiplier: 1.5, emergencyMultiplier: 2.0, skillLevel: 'standard' },
      { id: 'building_engineer_sr', name: 'Building Engineer (Senior)', hourlyRate: 55, overtimeMultiplier: 1.5, emergencyMultiplier: 2.0, skillLevel: 'senior' },
      { id: 'controls_specialist', name: 'Controls Specialist', hourlyRate: 85, overtimeMultiplier: 1.5, emergencyMultiplier: 2.0, skillLevel: 'specialist' },
      { id: 'electrician', name: 'Electrician', hourlyRate: 70, overtimeMultiplier: 1.5, emergencyMultiplier: 2.5, skillLevel: 'standard' },
      { id: 'hvac_tech', name: 'HVAC Technician', hourlyRate: 65, overtimeMultiplier: 1.5, emergencyMultiplier: 2.0, skillLevel: 'standard' },
      { id: 'plumber', name: 'Plumber', hourlyRate: 70, overtimeMultiplier: 1.5, emergencyMultiplier: 2.0, skillLevel: 'standard' },
      { id: 'elevator_mechanic', name: 'Elevator Mechanic (vendor)', hourlyRate: 175, overtimeMultiplier: 1.5, emergencyMultiplier: 2.0, skillLevel: 'vendor' },
      { id: 'fls_inspector', name: 'Fire/Life-Safety Inspector (vendor)', hourlyRate: 130, overtimeMultiplier: 1.5, emergencyMultiplier: 2.0, skillLevel: 'vendor' },
    ],
  },
}

export default facilityManagementConfig
